// Naming a period, and marking that it came round (1.17.0, ADR-0068).
//
// Two acts, two nouns, both declared in Phase 0 and neither ever written. They
// build events and never touch the store, like every other intent module.
//
// **Nothing here can create a demand.** `anchor` is demand-free (law 6), so the
// gate refuses a clock on one — there is no date, no route, no return. An
// anchor is a name for a period, not a thing to do.

import type { AppEvent, NodeId } from '../events.ts';
import type { StampContext } from './session.ts';

const base = (ctx: StampContext, kind: string, node: string | null, payload: unknown): AppEvent => ({
  id: ctx.id(), vault: ctx.vault, at: ctx.at, device: ctx.device, seq: ctx.seq(),
  kind, node, payload,
} as AppEvent);

/** One line of text, the same cleaner shape capture and the load entry use. */
const clean = (s: string): string => s.replace(/[ --]/g, ' ').trim().slice(0, 200);

/**
 * Name a period.
 *
 * `recurrence` is kept as typed and **nothing derives from it** — see
 * `src/anchors.ts`. An empty one is ordinary: plenty of periods have a name and
 * no rhythm anybody wants to write down.
 */
export function defineAnchorEvents(
  ctx: StampContext, node: NodeId, name: string, recurrence: string,
): AppEvent[] {
  const clean_ = clean(name);
  if (!clean_) return [];
  return [base(ctx, 'anchor.defined', node, { name: clean_, recurrence: clean(recurrence) })];
}

/**
 * It came round — mark it.
 *
 * **The watermark is the whole point, and it is why anchors could ship at all.**
 * ADR-0057 deferred them partly because `anchor.fired` carried only a time, and
 * a time-only cut silently drops another device's history: a shard can deliver
 * work stamped before your last meeting that you had never seen. That was an
 * audit finding on the export path, and `reportedBefore` exists because of it.
 *
 * So a firing carries the same per-device high-water mark a report does, read
 * from the state the person is actually looking at. Same field, same reader,
 * no second mechanism.
 *
 * Firing is always an ACT. Nothing fires an anchor on a schedule: an anchor that
 * fired itself would be a nag with a calendar, and this app does not have one.
 */
export function fireAnchorEvents(
  ctx: StampContext, anchor: NodeId, mark: Record<string, number>,
): AppEvent[] {
  return [base(ctx, 'anchor.fired', null, { anchor, at: ctx.at, upToSeqByDevice: { ...mark } })];
}
